import { checkInOut } from "@/lib/api/clients/GuestClient";
import { isValidObjectId } from "@/lib/utils/input-validator";

/**
 * Check a party in or out, one person at a time.
 *
 * ONE request per person, strictly sequential, spaced. Not a `Promise.all`:
 * `PUT /check-in-out` is rate-limited to 10 requests per SECOND per caller
 * (bucket `checkin-scan`, fixed window; without a device header the key falls
 * back to `user:<id>`, so every check-in this operator makes shares it). A
 * parallel fan-out over a page selection 429'd everything past the first
 * window in prod (2026-07-16) — which is why every BULK door in this app went
 * to the background job instead.
 *
 * A party is not a page selection, and the job is not usable here:
 *  - the job's submit endpoint requires `guests:write` and excludes
 *    EVENT-CUSTOM roles, so a door role that may check people in one by one
 *    would get a 403 at some arbitrary party size;
 *  - the job path does not clear `autoCheckoutAt` (the single endpoint does),
 *    so the same party would behave differently either side of the threshold;
 *  - and nothing refreshes the guest list when the job finishes.
 * So: one lane, paced by `CALL_SPACING_MS`.
 *
 * NOT bounded here. `PARTY_MAX` is a rule the party SHEET applies to a party
 * (`PCheckInParty` refuses above it); this helper has no cap of its own, and
 * `PCheckInPicker` — whose selection grows a page at a time — imposes none
 * either. At 150 ms a hundred ticks is fifteen seconds of a modal that is
 * deliberately locked shut while it writes. An earlier version of this comment
 * claimed the bound lived here; it does not, and a caller that can select
 * without limit owes its operator either a cap or a progress line.
 *
 * Deliberately calls the CLIENT and not the `GuestHandling` handler. The
 * handler funnels every failure through `handleErrorServer`, which keeps
 * `message` but drops `status` and the response body — and this endpoint puts
 * the reason for its 409s in `error`, not in `errorCode`, so after that
 * conversion a capacity block is indistinguishable from a double scan.
 */

/** Gap between two writes. 25 people ≈ 3.8 s ⇒ at most ~7 in any 1 s window. */
export const CALL_SPACING_MS = 150;
/** Upper bound for a single 429 back-off, seconds. */
const MAX_RETRY_WAIT_S = 3;

export type PartyCheckInErrorKind =
  | "capacity"
  | "rateLimited"
  | "notAllowed"
  | "outOfScope"
  /**
   * The EVENT is read-only, not the caller. `Filters` (main-backend) answers
   * 403 `eventArchived` / `eventPlanLocked` to every POST/PUT/PATCH/DELETE on
   * such an event, and `PUT /check-in-out` is a PUT — so an OWNER clicking
   * check-in on an archived or expired event used to be told "you do not have
   * permission", which is the one reading that is certainly wrong.
   */
  | "eventArchived"
  | "eventPlanLocked"
  | "unknown";

export type PartyCheckInResult = {
  guestId: string;
  /**
   * `done` — the flag flipped. `already` — the server refused because the guest
   * was ALREADY in the target state (someone at the door got there first);
   * nothing is wrong, so it is not counted as a failure. `failed` — see `error`.
   */
  status: "done" | "already" | "failed";
  /** Checked in over a WARNING-mode capacity rule: it worked, and it was over. */
  warning?: "capacity";
  error?: PartyCheckInErrorKind;
};

export type PartyCheckInReport = {
  results: PartyCheckInResult[];
  /**
   * Why the run stopped before reaching everyone. A BLOCK-mode capacity rule
   * refuses every further guest for the same reason, and a caller without the
   * token-minting capability gets the same 401 for every person — continuing
   * would burn rate-limit budget to print the same message N times.
   */
  stopped?: PartyCheckInErrorKind;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type ApiError = Error & {
  status?: number;
  retryAfter?: number;
  response?: { data?: { error?: string; errorCode?: string } };
};

/** The reason a 4xx carries — `error` for this endpoint's conflicts, `errorCode` elsewhere. */
function reasonOf(err: ApiError): string {
  const data = err.response?.data;
  return data?.error || data?.errorCode || err.message || "";
}

/**
 * Does this refusal apply to the NEXT guest too?
 *
 * A kind is not only a sentence — it is a control-flow decision, and this file
 * owns the union so it owns both. Written as an exhaustive `switch` on purpose:
 * an open `if` chain answers "keep going" for a kind nobody thought about, and
 * that is exactly how the two event locks — which refuse every guest, forever,
 * until somebody un-archives the event — came to be retried once per selected
 * guest, spending the shared `checkin-scan` budget to print one sentence N
 * times.
 */
export function stopsTheRun(error: PartyCheckInErrorKind | undefined): boolean {
  switch (error) {
    // The event is read-only. Nothing about the next guest changes that.
    case "eventArchived":
    case "eventPlanLocked":
    // A caller who may not check this guest in may not check in the next one
    // either. Capacity is very nearly the same: `CheckInRuleService` still
    // admits a guest matching the rule's exception conditions, so a 409 for a
    // standard guest does not strictly prove the next one is refused — but
    // stopping is right in the ordinary case, the sheet stays open, and the
    // unwritten members stay ticked, so one more click gets an exempt guest in.
    case "capacity":
    case "notAllowed":
      return true;
    // About ONE guest: the next one may well succeed.
    case "outOfScope":
    // Already retried once inside the loop; a second guest gets its own window.
    case "rateLimited":
    case "unknown":
    case undefined:
      return false;
    default: {
      // A new kind must be decided here, not defaulted. This line stops
      // compiling the moment the union grows.
      const exhaustive: never = error;
      void exhaustive;
      // A kind that got past the types is not a licence to keep writing.
      return false;
    }
  }
}

/**
 * Does this failure earn one more attempt?
 *
 * A switch for the same reason `stopsTheRun` is one: the safe default here is
 * "no", so an open predicate would not misbehave — it would just stop being a
 * decision. Both readings of a kind belong in the file that owns the union.
 */
export function retriesOnce(error: PartyCheckInErrorKind | undefined): boolean {
  switch (error) {
    // The window is a second and the door is waiting.
    case "rateLimited":
      return true;
    // Every other failure is a decision, not a delay — retrying only spends
    // the budget again for the same answer.
    case "capacity":
    case "notAllowed":
    case "outOfScope":
    case "eventArchived":
    case "eventPlanLocked":
    case "unknown":
    case undefined:
      return false;
    default: {
      const exhaustive: never = error;
      void exhaustive;
      return false;
    }
  }
}

function classify(err: ApiError): PartyCheckInErrorKind {
  const reason = reasonOf(err);
  if (reason === "CAPACITY_EXCEEDED") return "capacity";
  if (err.status === 429 || reason === "rateLimited") return "rateLimited";
  // 401 QR_AUTH_FAILED (the caller may not mint a token, so it may never check
  // anyone in tokenlessly) and 403 are the same thing to the operator.
  // Ahead of the blanket 403: the reason is on the envelope, and these two are
  // about the EVENT rather than the role.
  if (reason === "eventArchived") return "eventArchived";
  if (reason === "eventPlanLocked") return "eventPlanLocked";
  if (err.status === 401 || err.status === 403 || reason === "QR_AUTH_FAILED")
    return "notAllowed";
  // A row-scoped role gets 404 `guestNotInEvent` for a guest outside its scope —
  // existence-hiding by design.
  if (err.status === 404 || reason === "guestNotInEvent") return "outOfScope";
  return "unknown";
}

async function writeOne(
  eventId: string,
  guestId: string,
  checkIn: boolean,
  performedByType: string,
): Promise<{ result: PartyCheckInResult; retryAfterS?: number }> {
  try {
    const res = await checkInOut(eventId, guestId, checkIn, performedByType);
    // WARNING-mode capacity answers 200 with a body — success, but say so.
    if (res && typeof res === "object" && res.warning === "CAPACITY_EXCEEDED") {
      return { result: { guestId, status: "done", warning: "capacity" } };
    }
    return { result: { guestId, status: "done" } };
  } catch (e) {
    const err = e as ApiError;
    // The server refuses a flip that would not change anything: a companion the
    // door already scanned. The operator's intent is satisfied either way.
    if (reasonOf(err) === "ALREADY_CHECKED_IN") {
      return { result: { guestId, status: "already" } };
    }
    return {
      result: { guestId, status: "failed", error: classify(err) },
      // Sent as a header by the limiter and lifted onto the error by the api
      // shim; the window is a second, so this is normally 1.
      retryAfterS: typeof err.retryAfter === "number" ? err.retryAfter : undefined,
    };
  }
}

/**
 * Runs the writes. `onWritten` fires per person the moment the server confirmed,
 * so the rows behind the dialog flip progressively rather than all at the end.
 */
export async function runPartyCheckIn(
  eventId: string,
  guestIds: string[],
  checkIn: boolean,
  performedByType: string = "Admin Suite",
  onWritten?: (guestId: string) => void,
): Promise<PartyCheckInReport> {
  const results: PartyCheckInResult[] = [];

  for (let i = 0; i < guestIds.length; i++) {
    const guestId = guestIds[i];
    if (!isValidObjectId(eventId) || !isValidObjectId(guestId)) {
      results.push({ guestId, status: "failed", error: "unknown" });
      continue;
    }
    if (i > 0) await sleep(CALL_SPACING_MS);

    const first = await writeOne(eventId, guestId, checkIn, performedByType);
    let result = first.result;

    // One retry for a full bucket, and only for that: the window is a second,
    // the door is waiting, and every other failure is a decision, not a delay.
    if (retriesOnce(result.error)) {
      const waitS = Math.min(first.retryAfterS ?? 1, MAX_RETRY_WAIT_S);
      await sleep(Math.max(waitS, 0) * 1000);
      ({ result } = await writeOne(eventId, guestId, checkIn, performedByType));
    }

    results.push(result);
    if (result.status !== "failed") onWritten?.(guestId);

    if (stopsTheRun(result.error)) return { results, stopped: result.error };
  }

  return { results };
}
